class ProductRecommendations extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
    this.#wireCarouselArrows();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
          requestAnimationFrame(() => this.#wireCarouselArrows());
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Move one product per click (step is always 1).
   */
  #getCarouselStep() {
    return 1;
  }

  /**
   * Get number of visible slides (columns): desktop default 4, mobile 2.
   * Used only to disable Next when the last visible set is shown.
   */
  #getVisibleCount(slideshow) {
    const visible = slideshow.visibleSlides;
    if (visible?.length > 0) return visible.length;
    return typeof window !== 'undefined' && window.matchMedia('(width < 750px)').matches ? 2 : 4;
  }

  /**
   * Update Previous/Next disabled: Previous at start, Next when last visible set is shown.
   */
  #updateCarouselArrowStates(container, slideshow) {
    const prev = container.querySelector('.slideshow-control--previous');
    const next = container.querySelector('.slideshow-control--next');
    if (!prev || !next) return;
    const slides = slideshow.slides;
    const current = slideshow.current;
    const infinite = slideshow.infinite;
    const visibleCount = this.#getVisibleCount(slideshow);
    const lastPageStart = slides?.length ? Math.max(0, slides.length - visibleCount) : 0;
    prev.disabled = !infinite && current <= 0;
    next.disabled = !infinite && slides && current >= lastPageStart;
  }

  /**
   * Wire external carousel arrows to the slideshow.
   * Move one product per click; disable Previous at start, Next when last visible set is shown.
   */
  #wireCarouselArrows() {
    const arrowsContainers = this.querySelectorAll('[data-carousel-arrows]');
    for (const container of arrowsContainers) {
      const slideshowId = container.dataset.slideshowId;
      let slideshow = slideshowId ? this.querySelector(`#${CSS.escape(slideshowId)}`) : null;
      if (!slideshow) {
        slideshow = this.querySelector('.resource-list__carousel slideshow-component');
      }
      if (!slideshow || typeof slideshow.select !== 'function') continue;

      const onSelect = () => this.#updateCarouselArrowStates(container, slideshow);

      container.addEventListener('click', (e) => {
        const prev = e.target.closest('.slideshow-control--previous');
        const next = e.target.closest('.slideshow-control--next');
        if (prev) {
          e.preventDefault();
          e.stopPropagation();
          if (prev.disabled) return;
          const step = this.#getCarouselStep();
          const nextIndex = Math.max(0, slideshow.current - step);
          slideshow.select(nextIndex, e);
          onSelect();
        } else if (next) {
          e.preventDefault();
          e.stopPropagation();
          if (next.disabled) return;
          const slides = slideshow.slides;
          const step = this.#getCarouselStep();
          const nextIndex = slides ? Math.min(slides.length - 1, slideshow.current + step) : 0;
          slideshow.select(nextIndex, e);
          onSelect();
        }
      });

      slideshow.addEventListener('slideshow:select', onSelect);
      onSelect();
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    // Silent error handling
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
